/**
 * DocumentProcessingDialog Component
 *
 * Full-screen modal dialog for document processing
 * Can be triggered from any page via global toolbar/shortcut
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useDocumentProcessing } from '@/hooks/useDocumentProcessing'
import { DocumentUploader } from './DocumentUploader'
import type { Document2WithEntities } from '@/lib/api/client'

interface DocumentProcessingDialogProps {
  /** Is dialog open */
  isOpen: boolean

  /** Close dialog */
  onClose: () => void

  /** Callback when document is created and processed */
  onDocumentCreated?: (doc: Document2WithEntities) => void

  /** Dialog title (optional) */
  title?: string

  /** Dialog description (optional) */
  description?: string
}

export function DocumentProcessingDialog({
  isOpen,
  onClose,
  onDocumentCreated,
  title = 'Upload & Analyze Document',
  description = 'Upload a document to extract entities and relationships with evidence quotes',
}: DocumentProcessingDialogProps) {
  const {
    uploadDocument,
    pasteContent,
    extractEntities,
    extractionProgress,
    isConfigured,
    error,
    clearError,
  } = useDocumentProcessing({
    autoLoadDocuments: false, // Don't auto-load in modal
    onDocumentCreated: (doc) => {
      onDocumentCreated?.(doc)
      // Auto-close dialog on successful upload
      if (extractionProgress.stage === 'complete') {
        onClose()
      }
    },
    onError: (err) => {
      console.error('Document processing error:', err)
    },
  })

  const handleComplete = (doc: Document2WithEntities) => {
    onDocumentCreated?.(doc)
    onClose()
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      clearError()
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <DocumentUploader
          onUpload={uploadDocument}
          onPaste={pasteContent}
          onExtract={extractEntities}
          extractionProgress={extractionProgress}
          isConfigured={isConfigured}
          error={error}
          onComplete={handleComplete}
          onClose={onClose}
        />
      </DialogContent>
    </Dialog>
  )
}
